import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ExcelJS from "exceljs";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useLoader } from "../../../context/LoaderContext";
import { useUserData } from "../../../hooks/useUserData";
import { fetchStaffLookup, filterAssignableStaff } from "../../../services/lookupService";
import utils from "../../../utils/utils";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import {
  assignDocumentLibraryStaff,
  assignMyLearningStaff,
  fetchDocumentCategories,
  fetchDocumentLibraryDocumentById,
  fetchDocumentLibraryPage,
  fetchDocumentLibraryStaffPicker,
  fetchDocumentTypes,
  fetchMyLearningStaff,
  fetchMyLearningStaffPicker,
  removeDocumentLibraryStaff,
  removeMyLearningStaffV2,
  saveMyLearningStaff,
  sendMyLearningReminder,
} from "./qmsService";
import {
  mapMyLearningLibraryStaffRows,
  parseMyLearningStaffPickerResponse,
  promptDiscardMyLearningChanges,
  serializeMyLearningAssignmentPayload,
} from "./qmsPublishStaffUtils";
import {
  getApiTable,
  getPaginationInfo,
  isApiSuccess,
  apiMessage,
  normalizeSuccessStoryList,
} from "./qmsApiUtils";
import { getQmsAccessState, useQmsAccess } from "./qmsPermissionUtils";
import { getQmsScreen } from "./qmsScreenConfig";
import { formatReviewDateCell, formatReviewDateDisplay } from "./qmsReviewDateUtils";
import { LIBRARY_LEARNING_PROGRESS_FILTER_OPTIONS } from "./qmsDocumentLibraryLearningUtils";
import QmsDocumentLibraryWorkspaceModal from "./QmsDocumentLibraryWorkspaceModal";
import QmsDocumentLibraryProgressCell from "./QmsDocumentLibraryProgressCell";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildQmsDocumentLibraryTourSteps } from "./qmsDocumentLibraryTourSteps";
import {
  hasCompletedQmsDocLibTour,
  markQmsDocLibTourCompleted,
} from "./qmsDocumentLibraryTourStorage";
import { QMS_DOC_LIBRARY_ACCESS_REVOKED_MESSAGE } from "./qmsDocumentLibraryMessages";
import DateRangeFilter from "../../common/DateRangeFilter";
import { WORKSPACE_PANELS } from "./qmsDocumentWorkspacePanels";
import "./QmsPages.scss";

const DEFAULT_PAGE_SIZE = 25;
const screen = getQmsScreen("documentLibrary");

const LIBRARY_FIELD_OPTIONS = [
  { key: "DocumentId", label: "ID" },
  { key: "DocumentTitle", label: "Document" },
  { key: "DocumentType", label: "Type" },
  { key: "DocumentCategoryAbberiviation", label: "Category" },
  { key: "DocumentSize", label: "File Size" },
  { key: "IsReadingMandatory", label: "Mandatory Reading" },
  { key: "IsQuizApplicable", label: "Quiz Applied" },
  { key: "ReviewDate", label: "Review Date" },
  { key: "ReviewDueStatus", label: "Learning progress" },
  { key: "CreatedBy", label: "Owner" },
  { key: "CreatedDateTime", label: "Created Date" },
  { key: "PublishedBy", label: "Published By" },
  { key: "PublishedOn", label: "Published Date" },
];

const DEFAULT_LIBRARY_COLUMNS = [
  "DocumentId",
  "DocumentTitle",
  "IsReadingMandatory",
  "IsQuizApplicable",
  "ReviewDate",
  "ReviewDueStatus",
  "PublishedBy",
  "PublishedOn",
];

const DOC_LIB_TOUR_IDS = {
  root: "qms-doc-lib-table",
  thead: "qms-doc-lib-columns",
  footer: "qms-doc-lib-footer",
  export: "qms-doc-lib-export",
  resetLayout: "qms-doc-lib-reset-layout",
  addColumn: "qms-doc-lib-add-column",
  addColumnField: "DocumentTitle",
  freezeColumn: "qms-doc-lib-freeze-column",
  freezeColumnField: "DocumentTitle",
  dragColumn: "qms-doc-lib-drag-column",
  dragColumnField: "DocumentTitle",
  filters: {
    DocumentTitle: "qms-doc-lib-filter-document",
  },
  columns: {
    DocumentId: "qms-doc-lib-id-col",
    ReviewDate: "qms-doc-lib-review-date-col",
    ReviewDueStatus: "qms-doc-lib-review-status-col",
  },
};

const LIBRARY_SERVER_SEARCH_FIELDS = [
  "DocumentId",
  "DocumentTitle",
  "CreatedBy",
  "IsReadingMandatory",
  "IsQuizApplicable",
];

const EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const COLUMN_DATE_PRESETS = [
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

function getDocumentListId(row) {
  const typeAbbr = row.DocumentTypeAbberiviation || "";
  const catAbbr = row.DocumentCategoryAbberiviation || "";
  const uniqueNum = row.DocumentCategoryUniqueNumber || "";
  const version = row.VersionNumber ?? "";
  if (!typeAbbr && !catAbbr && !uniqueNum && version === "") return "—";
  return `${typeAbbr}_${catAbbr}_${uniqueNum}_V${version}`;
}

function getDocumentCode(row) {
  return getDocumentListId(row);
}

function parseDocumentIdNumberSearch(value) {
  return String(value || "").replace(/\D/g, "");
}

function getDocumentDisplayName(row) {
  const title = String(row.DocumentTitle || "").trim();
  if (title) return title;

  const ext = row.DocumentExtension
    ? `.${String(row.DocumentExtension).replace(/^\./, "")}`
    : "";
  return ext ? `Untitled${ext}` : "—";
}

function formatDocumentSize(size) {
  const n = Number(size);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${n % 1 === 0 ? n : n.toFixed(1)} KB`;
  return `${(n / 1024).toFixed(1)} MB`;
}

function formatAbbrNameCell(abbr, fullName, maxLen = 30) {
  const text =
    abbr && fullName ? `${abbr} - ${fullName}` : fullName || abbr || "—";
  const display = text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
  return (
    <span className="qms-doc-lib__abbr-name" title={text}>
      {display}
    </span>
  );
}

function parseYesNoFilter(val) {
  const v = String(val || "").trim().toLowerCase();
  if (!v) return null;
  if (["yes", "y", "true", "1", "mandatory"].includes(v)) return true;
  if (["no", "n", "false", "0"].includes(v)) return false;
  return null;
}

function buildLibrarySearchText(columnSearches) {
  return String(columnSearches.DocumentTitle || "").trim();
}

function getSingleFilterId(csvValue) {
  const ids = String(csvValue || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  return ids.length === 1 ? ids[0] : "";
}

function isQmsHttpSuccess(response) {
  return Number(response?.success) === 1;
}

function formatYesNoCell(value) {
  const yes = Number(value) === 1 || value === true;
  return (
    <span className={`qms-doc-lib__yesno ${yes ? "is-yes" : "is-no"}`}>
      {yes ? "Yes" : "No"}
    </span>
  );
}

function formatYesNoExport(value) {
  const yes = Number(value) === 1 || value === true;
  return yes ? "Yes" : "No";
}

function getLibraryExportCellValue(row, field) {
  if (field === "DocumentId") return getDocumentListId(row);
  if (field === "DocumentTitle") return getDocumentDisplayName(row);
  if (field === "DocumentType") {
    const abbr = row.DocumentTypeAbberiviation || "";
    const name = row.DocumentType || "";
    return abbr && name ? `${abbr} - ${name}` : name || abbr || "";
  }
  if (field === "DocumentCategoryAbberiviation") {
    const abbr = row.DocumentCategoryAbberiviation || "";
    const name = row.DocumentCategory || "";
    return abbr && name ? `${abbr} - ${name}` : name || abbr || "";
  }
  if (field === "DocumentSize") return formatDocumentSize(row.DocumentSize);
  if (field === "ReviewDate") return formatReviewDateDisplay(row);
  if (field === "ReviewDueStatus") {
    const review = formatReviewDateCell(row);
    return review.badge || "—";
  }
  if (field === "CreatedBy") return String(row.CreatedBy || "");
  if (field === "CreatedDateTime") {
    return utils.formatDateTimeDisplay(row.CreatedDateTime);
  }
  if (field === "PublishedBy") return String(row.PublishedBy || "");
  if (field === "PublishedOn") {
    return utils.formatDateTimeDisplay(row.PublishedOn);
  }
  if (field === "IsReadingMandatory") {
    return formatYesNoExport(row.IsReadingMandatory);
  }
  if (field === "IsQuizApplicable") {
    return formatYesNoExport(row.IsQuizApplicable);
  }
  return String(row[field] ?? "");
}

const QmsDocumentLibrary = () => {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { showLoader, hideLoader } = useLoader();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSystemAdmin = userData?.Role === "System Administrator";
  const { canQmsLibraryAssignStaff, canQmsMyLearningAssignStaff } = useQmsAccess();
  const canManageStaff = isSystemAdmin || canQmsLibraryAssignStaff;
  const canManageLearningStaff = isSystemAdmin || canQmsMyLearningAssignStaff;
  const canSendLearningReminders = canManageLearningStaff;

  const organizationId = userData?.OrganizationId;
  const staffId = userData?.StaffId;

  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: "QMS_DOCUMENT_LIBRARY",
    loading: userLoading,
  });

  const [rows, setRows] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [apiFilters, setApiFilters] = useState({
    searchText: "",
    filterCreatedBy: "",
    filterReviewDueDateFrom: "",
    filterReviewDueDateTo: "",
    filterPublishedBy: "",
    filterCreatedDate: "",
    filterPublishedOn: "",
    filterDocumentIdNumber: "",
    filterCreatedDateFrom: "",
    filterCreatedDateTo: "",
    filterPublishedOnFrom: "",
    filterPublishedOnTo: "",
    isReadingMandatory: null,
    isQuizApplicable: null,
    filterDocumentTypeIds: "",
    filterDocumentCategoryIds: "",
    filterIsActive: "true",
    filterLearningProgress: "",
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [typesLoading, setTypesLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [publisherOptions, setPublisherOptions] = useState([]);
  const [publishedByFilter, setPublishedByFilter] = useState([]);
  const [reviewDateRange, setReviewDateRange] = useState(EMPTY_DATE_RANGE);
  const [createdDateRange, setCreatedDateRange] = useState(EMPTY_DATE_RANGE);
  const [publishedDateRange, setPublishedDateRange] = useState(EMPTY_DATE_RANGE);

  const [workspaceModal, setWorkspaceModal] = useState({
    open: false,
    row: null,
    panel: "preview",
    staff: [],
    staffLoading: false,
    savingStaffIds: [],
    learningStaff: [],
    learningStaffTrainingRoles: [],
    learningStaffLoading: false,
    learningStaffSaving: false,
    savingLearningStaffIds: [],
    learningProgressStaff: [],
    learningProgressLoading: false,
    remindingStaffIds: [],
  });
  const [showDocLibTour, setShowDocLibTour] = useState(false);

  const fetchSeqRef = useRef(0);
  const userDataRef = useRef(userData);
  const staffLoadedForRef = useRef(null);
  const staffFetchInFlightRef = useRef(false);
  const learningStaffLoadedForRef = useRef(null);
  const learningStaffFetchInFlightRef = useRef(false);
  const myLearningDraftDirtyRef = useRef(false);
  const learningProgressLoadedForRef = useRef(null);
  const learningProgressFetchInFlightRef = useRef(false);
  const deepLinkRequestRef = useRef(0);
  userDataRef.current = userData;

  const deepLinkDocumentId = String(searchParams.get("documentId") || "").trim();
  const deepLinkPanelParam = String(searchParams.get("panel") || "preview").trim();

  useEffect(() => {
    if (access.status !== "allowed" || !organizationId) return undefined;

    let cancelled = false;
    setTypesLoading(true);
    setCategoriesLoading(true);
    (async () => {
      try {
        const [typeRes, catRes, staffRows] = await Promise.all([
          fetchDocumentTypes(userDataRef.current),
          fetchDocumentCategories(userDataRef.current),
          fetchStaffLookup(userDataRef.current).catch(() => []),
        ]);
        if (cancelled) return;

        if (isQmsHttpSuccess(typeRes)) {
          setDocumentTypes(
            normalizeSuccessStoryList(typeRes).map((row) => ({
              id: String(row.DocumentTypeId),
              label: row.DocumentType,
              abbr: row.Abberiviation,
            })),
          );
        } else {
          setDocumentTypes([]);
        }

        if (isQmsHttpSuccess(catRes)) {
          setDocumentCategories(
            normalizeSuccessStoryList(catRes).map((row) => ({
              id: String(row.DocumentCategoryId),
              label: row.DocumentCategory,
              abbr: row.Abberiviation,
            })),
          );
        } else {
          setDocumentCategories([]);
        }

        const byName = new Map();
        (staffRows || []).forEach((staff) => {
          const name = String(staff?.StaffName || "").trim();
          if (!name) return;
          const key = name.toLowerCase();
          if (!byName.has(key)) byName.set(key, { value: name, label: name });
        });
        setPublisherOptions(
          [...byName.values()].sort((a, b) => a.label.localeCompare(b.label)),
        );
      } catch (error) {
        console.error("Document library filter metadata failed", error);
        if (!cancelled) {
          setDocumentTypes([]);
          setDocumentCategories([]);
          setPublisherOptions([]);
        }
      } finally {
        if (!cancelled) {
          setTypesLoading(false);
          setCategoriesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [access.status, organizationId]);

  useEffect(() => {
    if (access.status !== "allowed" || !organizationId || !staffId) {
      return undefined;
    }

    let cancelled = false;
    const seq = ++fetchSeqRef.current;

    const loadLibrary = async () => {
      setTableLoading(true);
      try {
        const response = await fetchDocumentLibraryPage(userDataRef.current, {
          pageNumber,
          pageSize,
          ...apiFilters,
        });
        if (cancelled || seq !== fetchSeqRef.current) return;

        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Failed to load documents.");
        }
        setRows(getApiTable(response, "DocumentLibraryDocuments"));
        const pagination = getPaginationInfo(response);
        setTotalCount(Number(pagination.TotalCount) || 0);
      } catch (error) {
        if (cancelled || seq !== fetchSeqRef.current) return;
        console.error("Document library load failed", error);
        showAlert({
          title: "Error",
          message: error?.message || "Failed to load document library.",
          type: "danger",
        });
        setRows([]);
        setTotalCount(0);
      } finally {
        if (!cancelled && seq === fetchSeqRef.current) {
          setTableLoading(false);
        }
      }
    };

    loadLibrary();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.status, organizationId, staffId, pageNumber, pageSize, apiFilters, refreshToken]);

  const handleRefresh = () => setRefreshToken((t) => t + 1);

  const handleExportToExcel = useCallback(
    async ({ columns }) => {
      if (!totalCount) {
        showAlert({
          title: "Export",
          message: "No documents to export for the current filters.",
          type: "info",
        });
        return;
      }

      const exportColumns = columns || DEFAULT_LIBRARY_COLUMNS;
      if (!exportColumns.length) {
        showAlert({
          title: "Export",
          message: "Add at least one data column before exporting.",
          type: "info",
        });
        return;
      }

      showLoader("Preparing Excel export...");
      try {
        const exportPageSize = Math.min(totalCount, 10000);
        const response = await fetchDocumentLibraryPage(userDataRef.current, {
          pageNumber: 1,
          pageSize: exportPageSize,
          ...apiFilters,
        });

        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Export failed.");
        }

        const exportRows = getApiTable(response, "DocumentLibraryDocuments");
        const headers = exportColumns.map(
          (field) =>
            LIBRARY_FIELD_OPTIONS.find((x) => x.key === field)?.label || field,
        );

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Document Library");
        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
        });

        exportRows.forEach((row) => {
          ws.addRow(
            exportColumns.map((field) => getLibraryExportCellValue(row, field)),
          );
        });

        ws.columns = headers.map((header) => ({
          header,
          key: header,
          width: Math.max(14, header.length + 2),
        }));

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `Document_Library_${new Date().toISOString().split("T")[0]}.xlsx`,
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Document library export failed", error);
        showAlert({
          title: "Export failed",
          message: error?.message || "Could not export document library.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [apiFilters, hideLoader, showAlert, showLoader, totalCount],
  );

  const openDocumentWorkspace = useCallback((row, panel = "preview") => {
    staffLoadedForRef.current = null;
    staffFetchInFlightRef.current = false;
    learningStaffLoadedForRef.current = null;
    learningStaffFetchInFlightRef.current = false;
    learningProgressLoadedForRef.current = null;
    learningProgressFetchInFlightRef.current = false;
    setWorkspaceModal({
      open: true,
      row,
      panel,
      staff: [],
      staffLoading: false,
      savingStaffIds: [],
      learningStaff: [],
      learningStaffTrainingRoles: [],
      learningStaffLoading: false,
      learningStaffSaving: false,
      savingLearningStaffIds: [],
      learningProgressStaff: [],
      learningProgressLoading: false,
      remindingStaffIds: [],
    });
  }, []);

  useEffect(() => {
    const panel = WORKSPACE_PANELS.some((item) => item.id === deepLinkPanelParam)
      ? deepLinkPanelParam
      : "preview";

    if (!deepLinkDocumentId) return undefined;
    if (access.status !== "allowed" || !organizationId || !staffId) return undefined;

    const requestId = ++deepLinkRequestRef.current;
    let cancelled = false;

    const openFromDeepLink = async () => {
      showLoader("Opening document...");
      try {
        const response = await fetchDocumentLibraryDocumentById(
          userDataRef.current,
          deepLinkDocumentId,
        );
        if (cancelled || requestId !== deepLinkRequestRef.current) return;

        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Document not found.");
        }

        const docRows = getApiTable(response, "DocumentLibraryDocuments");
        const row = docRows?.[0];
        if (!row) {
          throw new Error(QMS_DOC_LIBRARY_ACCESS_REVOKED_MESSAGE);
        }

        openDocumentWorkspace(row, panel);
        navigate("/qms/library", { replace: true });
      } catch (error) {
        if (cancelled || requestId !== deepLinkRequestRef.current) return;
        showAlert({
          title: "Document unavailable",
          message: error?.message || "Could not open document from link.",
          type: "warning",
        });
        navigate("/qms/library", { replace: true });
      } finally {
        if (requestId === deepLinkRequestRef.current) {
          hideLoader();
        }
      }
    };

    openFromDeepLink();

    return () => {
      cancelled = true;
    };
  }, [
    access.status,
    deepLinkDocumentId,
    deepLinkPanelParam,
    hideLoader,
    navigate,
    openDocumentWorkspace,
    organizationId,
    showAlert,
    showLoader,
    staffId,
  ]);

  const closeDocumentWorkspace = useCallback(() => {
    const finishClose = () => {
      myLearningDraftDirtyRef.current = false;
      staffLoadedForRef.current = null;
      staffFetchInFlightRef.current = false;
      learningStaffLoadedForRef.current = null;
      learningStaffFetchInFlightRef.current = false;
      learningProgressLoadedForRef.current = null;
      learningProgressFetchInFlightRef.current = false;
      setWorkspaceModal({
        open: false,
        row: null,
        panel: "preview",
        staff: [],
        staffLoading: false,
        savingStaffIds: [],
        learningStaff: [],
        learningStaffTrainingRoles: [],
        learningStaffLoading: false,
        learningStaffSaving: false,
        savingLearningStaffIds: [],
        learningProgressStaff: [],
        learningProgressLoading: false,
        remindingStaffIds: [],
      });
    };

    if (
      workspaceModal.panel === "learningStaff" &&
      myLearningDraftDirtyRef.current
    ) {
      promptDiscardMyLearningChanges(showAlert, finishClose);
      return;
    }

    finishClose();
  }, [showAlert, workspaceModal.panel]);

  const handleMyLearningDirtyChange = useCallback((dirty) => {
    myLearningDraftDirtyRef.current = dirty;
  }, []);

  const handleWorkspacePanelChange = useCallback(
    (panel) => {
      if (
        workspaceModal.panel === "learningStaff" &&
        panel !== "learningStaff" &&
        myLearningDraftDirtyRef.current
      ) {
        promptDiscardMyLearningChanges(showAlert, () => {
          myLearningDraftDirtyRef.current = false;
          setWorkspaceModal((prev) => ({ ...prev, panel }));
        });
        return;
      }

      if (panel !== "learningStaff") {
        myLearningDraftDirtyRef.current = false;
      }

      setWorkspaceModal((prev) => ({ ...prev, panel }));
    },
    [showAlert, workspaceModal.panel],
  );

  const handleServerColumnSearch = useCallback((columnSearches) => {
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      searchText: buildLibrarySearchText(columnSearches),
      filterDocumentIdNumber: parseDocumentIdNumberSearch(
        columnSearches.DocumentId,
      ),
      filterCreatedBy: String(columnSearches.CreatedBy || "").trim(),
      isReadingMandatory: parseYesNoFilter(columnSearches.IsReadingMandatory),
      isQuizApplicable: parseYesNoFilter(columnSearches.IsQuizApplicable),
    }));
  }, []);

  const handleColumnTypeFilter = useCallback((typeId) => {
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterDocumentTypeIds: typeId || "",
    }));
  }, []);

  const handleColumnCategoryFilter = useCallback((categoryId) => {
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterDocumentCategoryIds: categoryId || "",
    }));
  }, []);

  const handleLearningProgressFilter = useCallback((value) => {
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterLearningProgress: value || "",
    }));
  }, []);

  const publishedByFilterOptions = useMemo(() => {
    const byName = new Map(
      (publisherOptions || []).map((option) => [
        String(option.value).toLowerCase(),
        option,
      ]),
    );
    (rows || []).forEach((row) => {
      const name = String(row?.PublishedBy || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!byName.has(key)) byName.set(key, { value: name, label: name });
    });
    (publishedByFilter || []).forEach((name) => {
      const value = String(name || "").trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (!byName.has(key)) byName.set(key, { value, label: value });
    });
    return [...byName.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [publisherOptions, publishedByFilter, rows]);

  const handlePublishedByFilter = useCallback((values) => {
    const next = Array.isArray(values) ? values.filter(Boolean) : [];
    setPublishedByFilter(next);
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterPublishedBy: next.join("|"),
    }));
  }, []);

  const handleReviewDateRangeChange = useCallback((range) => {
    setReviewDateRange(range);
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterReviewDueDateFrom:
        range?.preset === "All Time" ? "" : range?.startDate || "",
      filterReviewDueDateTo:
        range?.preset === "All Time" ? "" : range?.endDate || "",
    }));
  }, []);

  const handleCreatedDateRangeChange = useCallback((range) => {
    setCreatedDateRange(range);
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterCreatedDateFrom:
        range?.preset === "All Time" ? "" : range?.startDate || "",
      filterCreatedDateTo:
        range?.preset === "All Time" ? "" : range?.endDate || "",
    }));
  }, []);

  const handlePublishedDateRangeChange = useCallback((range) => {
    setPublishedDateRange(range);
    setPageNumber(1);
    setApiFilters((prev) => ({
      ...prev,
      filterPublishedOnFrom:
        range?.preset === "All Time" ? "" : range?.startDate || "",
      filterPublishedOnTo:
        range?.preset === "All Time" ? "" : range?.endDate || "",
    }));
  }, []);

  const selectedColumnTypeId = useMemo(
    () => getSingleFilterId(apiFilters.filterDocumentTypeIds),
    [apiFilters.filterDocumentTypeIds],
  );

  const selectedColumnCategoryId = useMemo(
    () => getSingleFilterId(apiFilters.filterDocumentCategoryIds),
    [apiFilters.filterDocumentCategoryIds],
  );

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="report-table__column-search qms-doc-lib__column-filter"
            data-tour="qms-doc-lib-filter-id"
            placeholder="Search number"
            value={parseDocumentIdNumberSearch(options?.value)}
            disabled={options?.disabled}
            onChange={(event) => {
              options?.onChange?.(parseDocumentIdNumberSearch(event.target.value));
            }}
          />
        );
      }

      if (field === "DocumentType") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={selectedColumnTypeId}
            disabled={options?.disabled || typesLoading}
            onChange={(event) => handleColumnTypeFilter(event.target.value)}
          >
            <option value="">All types</option>
            {documentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.abbr ? `${type.abbr} - ${type.label}` : type.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "DocumentCategoryAbberiviation") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={selectedColumnCategoryId}
            disabled={options?.disabled || categoriesLoading}
            onChange={(event) => handleColumnCategoryFilter(event.target.value)}
          >
            <option value="">All categories</option>
            {documentCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.abbr
                  ? `${category.abbr} - ${category.label}`
                  : category.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "PublishedBy") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-filter-multiselect">
            <BadgeMultiSelect
              options={publishedByFilterOptions}
              value={publishedByFilter}
              onChange={handlePublishedByFilter}
              emptyLabel="All publishers"
              addLabel="+"
              popoverTitle="Filter by publisher"
              popoverSubtitle="Select one or more publishers"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "IsReadingMandatory" || field === "IsQuizApplicable") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
            aria-label={
              field === "IsReadingMandatory"
                ? "Filter by mandatory reading"
                : "Filter by quiz applied"
            }
          >
            <option value="">All</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        );
      }

      if (field === "ReviewDueStatus") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            data-tour="qms-doc-lib-filter-learning-progress"
            value={apiFilters.filterLearningProgress || ""}
            disabled={options?.disabled}
            onChange={(event) => handleLearningProgressFilter(event.target.value)}
          >
            {LIBRARY_LEARNING_PROGRESS_FILTER_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "ReviewDate") {
        if (options?.checkOnly) return true;
        return (
          <div
            className="qms-doc-lib__column-date-filter"
            data-tour="qms-doc-lib-filter-review-date"
          >
            <DateRangeFilter
              value={reviewDateRange}
              onChange={handleReviewDateRangeChange}
              placeholder="Review due range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "CreatedDateTime") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={createdDateRange}
              onChange={handleCreatedDateRangeChange}
              placeholder="Created date range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "PublishedOn") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={publishedDateRange}
              onChange={handlePublishedDateRangeChange}
              placeholder="Published date range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      return null;
    },
    [
      apiFilters.filterLearningProgress,
      categoriesLoading,
      createdDateRange,
      documentCategories,
      documentTypes,
      handleColumnCategoryFilter,
      handleColumnTypeFilter,
      handleCreatedDateRangeChange,
      handleLearningProgressFilter,
      handlePublishedByFilter,
      handlePublishedDateRangeChange,
      handleReviewDateRangeChange,
      publishedByFilter,
      publishedByFilterOptions,
      publishedDateRange,
      reviewDateRange,
      selectedColumnCategoryId,
      selectedColumnTypeId,
      typesLoading,
    ],
  );

  const loadStaffPicker = async (documentId) => {
    const response = await fetchDocumentLibraryStaffPicker(
      userDataRef.current,
      documentId,
    );
    if (!isApiSuccess(response)) {
      throw new Error(apiMessage(response) || "Failed to load staff.");
    }
    return filterAssignableStaff(getApiTable(response, "DocumentLibraryUsers"));
  };

  const loadLearningStaffPicker = async (documentId) => {
    const response = await fetchMyLearningStaffPicker(
      userDataRef.current,
      documentId,
    );
    if (!isApiSuccess(response)) {
      throw new Error(apiMessage(response) || "Failed to load My Learning staff.");
    }
    return parseMyLearningStaffPickerResponse(response);
  };

  const loadLearningProgressStaff = async (documentId) => {
    const response = await fetchMyLearningStaff(userDataRef.current, documentId);
    if (!isApiSuccess(response)) {
      throw new Error(apiMessage(response) || "Failed to load learning progress.");
    }
    return getApiTable(response, "MyLearningStaff");
  };

  useEffect(() => {
    const documentId = workspaceModal.row?.DocumentId;
    if (
      !workspaceModal.open ||
      workspaceModal.panel !== "libraryStaff" ||
      !documentId
    ) {
      return undefined;
    }
    if (
      staffLoadedForRef.current === documentId ||
      staffFetchInFlightRef.current
    ) {
      return undefined;
    }

    staffFetchInFlightRef.current = true;
    let cancelled = false;

    (async () => {
      setWorkspaceModal((prev) => ({ ...prev, staffLoading: true }));
      try {
        const staff = await loadStaffPicker(documentId);
        if (cancelled) return;
        staffLoadedForRef.current = documentId;
        setWorkspaceModal((prev) => ({
          ...prev,
          staff,
          staffLoading: false,
        }));
      } catch (error) {
        if (cancelled) return;
        showAlert({
          title: "Error",
          message: error?.message || "Failed to load staff.",
          type: "danger",
        });
        setWorkspaceModal((prev) => ({ ...prev, staffLoading: false }));
      } finally {
        if (!cancelled) {
          staffFetchInFlightRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
      staffFetchInFlightRef.current = false;
    };
  }, [
    workspaceModal.open,
    workspaceModal.panel,
    workspaceModal.row?.DocumentId,
  ]);

  useEffect(() => {
    const documentId = workspaceModal.row?.DocumentId;
    if (
      !workspaceModal.open ||
      workspaceModal.panel !== "learningStaff" ||
      !documentId
    ) {
      return undefined;
    }
    if (
      learningStaffLoadedForRef.current === documentId ||
      learningStaffFetchInFlightRef.current
    ) {
      return undefined;
    }

    learningStaffFetchInFlightRef.current = true;
    let cancelled = false;

    (async () => {
      setWorkspaceModal((prev) => ({ ...prev, learningStaffLoading: true }));
      try {
        const { staff: learningStaff, trainingRoles: learningStaffTrainingRoles } =
          await loadLearningStaffPicker(documentId);
        if (cancelled) return;
        learningStaffLoadedForRef.current = documentId;
        setWorkspaceModal((prev) => ({
          ...prev,
          learningStaff,
          learningStaffTrainingRoles,
          learningStaffLoading: false,
        }));
      } catch (error) {
        if (cancelled) return;
        showAlert({
          title: "Error",
          message: error?.message || "Failed to load My Learning staff.",
          type: "danger",
        });
        setWorkspaceModal((prev) => ({ ...prev, learningStaffLoading: false }));
      } finally {
        if (!cancelled) {
          learningStaffFetchInFlightRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
      learningStaffFetchInFlightRef.current = false;
    };
  }, [
    workspaceModal.open,
    workspaceModal.panel,
    workspaceModal.row?.DocumentId,
  ]);

  useEffect(() => {
    const documentId = workspaceModal.row?.DocumentId;
    if (
      !workspaceModal.open ||
      workspaceModal.panel !== "learningProgress" ||
      !documentId
    ) {
      return undefined;
    }
    if (
      learningProgressLoadedForRef.current === documentId ||
      learningProgressFetchInFlightRef.current
    ) {
      return undefined;
    }

    learningProgressFetchInFlightRef.current = true;
    let cancelled = false;

    (async () => {
      setWorkspaceModal((prev) => ({ ...prev, learningProgressLoading: true }));
      try {
        const learningProgressStaff = await loadLearningProgressStaff(documentId);
        if (cancelled) return;
        learningProgressLoadedForRef.current = documentId;
        setWorkspaceModal((prev) => ({
          ...prev,
          learningProgressStaff,
          learningProgressLoading: false,
        }));
      } catch (error) {
        if (cancelled) return;
        showAlert({
          title: "Error",
          message: error?.message || "Failed to load learning progress.",
          type: "danger",
        });
        setWorkspaceModal((prev) => ({ ...prev, learningProgressLoading: false }));
      } finally {
        if (!cancelled) {
          learningProgressFetchInFlightRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
      learningProgressFetchInFlightRef.current = false;
    };
  }, [
    workspaceModal.open,
    workspaceModal.panel,
    workspaceModal.row?.DocumentId,
  ]);

  const applyStaffChanges = useCallback(
    async (updates) => {
      const documentId = workspaceModal.row?.DocumentId;
      if (!documentId || !canManageStaff || !updates?.length) return;

      setWorkspaceModal((prev) => ({
        ...prev,
        savingStaffIds: updates.map(({ staffRow }) => staffRow.StaffId),
      }));

      try {
        for (const { staffRow, assign } of updates) {
          const response = assign
            ? await assignDocumentLibraryStaff(
                userDataRef.current,
                documentId,
                staffRow.StaffId,
              )
            : await removeDocumentLibraryStaff(
                userDataRef.current,
                documentId,
                staffRow.StaffId,
              );
          if (!isApiSuccess(response)) {
            throw new Error(apiMessage(response) || "Staff update failed.");
          }
        }

        const staff = await loadStaffPicker(documentId);
        setWorkspaceModal((prev) => ({
          ...prev,
          staff,
          staffLoading: false,
          savingStaffIds: [],
        }));
      } catch (error) {
        showAlert({
          title: "Error",
          message: error?.message || "Failed to update staff assignment.",
          type: "danger",
        });
        setWorkspaceModal((prev) => ({ ...prev, savingStaffIds: [] }));
      }
    },
    [canManageStaff, showAlert, workspaceModal.row?.DocumentId],
  );

  const handleStaffToggle = useCallback(
    (staffRow, assign) => applyStaffChanges([{ staffRow, assign }]),
    [applyStaffChanges],
  );

  const handleBulkStaffToggle = useCallback(
    (staffRows, assign) =>
      applyStaffChanges(staffRows.map((staffRow) => ({ staffRow, assign }))),
    [applyStaffChanges],
  );

  const handleMyLearningSaveDraft = useCallback(
    async (draft) => {
      const documentId = workspaceModal.row?.DocumentId;
      if (!documentId || !canManageLearningStaff) return;

      setWorkspaceModal((prev) => ({ ...prev, learningStaffSaving: true }));
      try {
        const payload = serializeMyLearningAssignmentPayload(
          draft,
          workspaceModal.learningStaff,
          workspaceModal.learningStaffTrainingRoles,
        );
        const response = await saveMyLearningStaff(userDataRef.current, {
          documentId,
          staffIds: payload.staffIds,
        });
        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "My Learning save failed.");
        }

        const { staff: learningStaff, trainingRoles: learningStaffTrainingRoles } =
          await loadLearningStaffPicker(documentId);
        learningStaffLoadedForRef.current = documentId;
        setWorkspaceModal((prev) => ({
          ...prev,
          learningStaff,
          learningStaffTrainingRoles,
          learningStaffLoading: false,
          learningStaffSaving: false,
        }));
      } catch (error) {
        setWorkspaceModal((prev) => ({ ...prev, learningStaffSaving: false }));
        showAlert({
          title: "Error",
          message: error?.message || "Failed to save My Learning assignment.",
          type: "danger",
        });
        throw error;
      }
    },
    [
      canManageLearningStaff,
      showAlert,
      workspaceModal.learningStaff,
      workspaceModal.learningStaffTrainingRoles,
      workspaceModal.row?.DocumentId,
    ],
  );

  const applyLearningStaffChanges = useCallback(
    async (updates) => {
      const documentId = workspaceModal.row?.DocumentId;
      if (!documentId || !canManageLearningStaff || !updates?.length) return;

      setWorkspaceModal((prev) => ({
        ...prev,
        savingLearningStaffIds: updates.map(({ staffRow }) => staffRow.StaffId),
      }));

      try {
        for (const { staffRow, assign } of updates) {
          const response = assign
            ? await assignMyLearningStaff(
                userDataRef.current,
                documentId,
                staffRow.StaffId,
              )
            : await removeMyLearningStaffV2(
                userDataRef.current,
                documentId,
                staffRow.StaffId,
              );
          if (!isApiSuccess(response)) {
            throw new Error(apiMessage(response) || "My Learning staff update failed.");
          }
        }

        const { staff: learningStaff, trainingRoles: learningStaffTrainingRoles } =
          await loadLearningStaffPicker(documentId);
        setWorkspaceModal((prev) => ({
          ...prev,
          learningStaff,
          learningStaffTrainingRoles,
          learningStaffLoading: false,
          savingLearningStaffIds: [],
        }));
      } catch (error) {
        showAlert({
          title: "Error",
          message: error?.message || "Failed to update My Learning staff assignment.",
          type: "danger",
        });
        setWorkspaceModal((prev) => ({ ...prev, savingLearningStaffIds: [] }));
      }
    },
    [canManageLearningStaff, showAlert, workspaceModal.row?.DocumentId],
  );

  const handleLearningStaffToggle = useCallback(
    (staffRow, assign) => applyLearningStaffChanges([{ staffRow, assign }]),
    [applyLearningStaffChanges],
  );

  const handleBulkLearningStaffToggle = useCallback(
    (staffRows, assign) =>
      applyLearningStaffChanges(
        staffRows.map((staffRow) => ({ staffRow, assign })),
      ),
    [applyLearningStaffChanges],
  );

  const sendLearningReminders = useCallback(
    async (staffRows) => {
      const documentId = workspaceModal.row?.DocumentId;
      if (!documentId || !canSendLearningReminders || !staffRows?.length) return;

      const staffIds = staffRows.map((row) => row.StaffId).filter(Boolean);
      setWorkspaceModal((prev) => ({
        ...prev,
        remindingStaffIds: staffIds,
      }));

      try {
        const response = await sendMyLearningReminder(
          userDataRef.current,
          documentId,
          staffIds,
        );
        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Could not send reminder.");
        }
        showAlert({
          title: "Reminder sent",
          message:
            apiMessage(response) ||
            `Reminder email queued for ${staffIds.length} staff member(s).`,
          type: "success",
        });
      } catch (error) {
        showAlert({
          title: "Reminder failed",
          message: error?.message || "Could not send learning reminder.",
          type: "danger",
        });
      } finally {
        setWorkspaceModal((prev) => ({ ...prev, remindingStaffIds: [] }));
      }
    },
    [canSendLearningReminders, showAlert, workspaceModal.row?.DocumentId],
  );

  const handleSendLearningReminder = useCallback(
    (staffRow) => sendLearningReminders([staffRow]),
    [sendLearningReminders],
  );

  const handleSendBulkLearningReminder = useCallback(
    (staffRows) => sendLearningReminders(staffRows),
    [sendLearningReminders],
  );

  const handleOpenLearningProgress = useCallback(
    (row) => openDocumentWorkspace(row, "learningProgress"),
    [openDocumentWorkspace],
  );

  const firstRowDocumentId = rows[0]?.DocumentId;

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "DocumentId") {
        return (
          <span className="qms-doc-lib__doc-id" title={getDocumentListId(row)}>
            {getDocumentListId(row)}
          </span>
        );
      }
      if (field === "DocumentTitle") {
        const name = getDocumentDisplayName(row);
        const isFirstRow = row.DocumentId === firstRowDocumentId;
        return (
          <button
            type="button"
            className="report-table__cell-link qms-doc-lib__doc-title-link"
            title={`View ${name}`}
            data-tour={isFirstRow ? "qms-doc-lib-doc-link" : undefined}
            onClick={() => openDocumentWorkspace(row, "preview")}
          >
            {name}
          </button>
        );
      }
      if (field === "DocumentType") {
        return formatAbbrNameCell(
          row.DocumentTypeAbberiviation,
          row.DocumentType,
        );
      }
      if (field === "DocumentCategoryAbberiviation") {
        return formatAbbrNameCell(
          row.DocumentCategoryAbberiviation,
          row.DocumentCategory,
        );
      }
      if (field === "DocumentSize") {
        return formatDocumentSize(row.DocumentSize);
      }
      if (field === "ReviewDate") {
        const review = formatReviewDateCell(row);
        return (
          <div
            className={`qms-doc-lib__review-cell is-${review.status || "none"}`}
          >
            <div className="qms-doc-lib__review-line">
              <span className="qms-doc-lib__review-duration">{review.primary}</span>
              {review.secondary ? (
                <>
                  <span className="qms-doc-lib__review-sep">·</span>
                  <span className="qms-doc-lib__review-due-date">{review.secondary}</span>
                </>
              ) : null}
            </div>
          </div>
        );
      }
      if (field === "ReviewDueStatus") {
        return (
          <QmsDocumentLibraryProgressCell
            row={row}
            onOpenProgress={handleOpenLearningProgress}
          />
        );
      }
      if (field === "CreatedBy") {
        return String(row.CreatedBy || "—");
      }
      if (field === "CreatedDateTime") {
        return utils.formatDateTimeDisplay(row.CreatedDateTime);
      }
      if (field === "PublishedBy") {
        return String(row.PublishedBy || "—");
      }
      if (field === "PublishedOn") {
        return utils.formatDateTimeDisplay(row.PublishedOn);
      }
      if (field === "IsReadingMandatory") {
        return formatYesNoCell(row.IsReadingMandatory);
      }
      if (field === "IsQuizApplicable") {
        return formatYesNoCell(row.IsQuizApplicable);
      }
      return String(row[field] ?? "—");
    },
    [firstRowDocumentId, handleOpenLearningProgress, openDocumentWorkspace],
  );

  const serverPagination = useMemo(
    () => ({
      page: pageNumber,
      pageSize,
      totalCount,
      loading: tableLoading,
      onPageChange: setPageNumber,
      onPageSizeChange: (size) => {
        setPageNumber(1);
        setPageSize(size);
      },
    }),
    [pageNumber, pageSize, totalCount, tableLoading],
  );

  const layoutStorageKey = useMemo(
    () => (staffId ? `qms-doc-lib-layout-v2-${staffId}` : ""),
    [staffId],
  );

  const docLibTourSteps = useMemo(
    () =>
      buildQmsDocumentLibraryTourSteps({
        hasRows: rows.length > 0,
      }),
    [rows.length],
  );

  useEffect(() => {
    if (access.status !== "allowed" || !staffId || tableLoading) return undefined;
    if (hasCompletedQmsDocLibTour(staffId)) return undefined;
    const timer = window.setTimeout(() => setShowDocLibTour(true), 900);
    return () => window.clearTimeout(timer);
  }, [access.status, staffId, tableLoading]);

  if (access.status === "loading") {
    return (
      <div className="user-mgmt-page qms-document-library">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: screen?.title || "Document Library" },
          ]}
        />
        <p className="text-muted mb-0">Loading…</p>
      </div>
    );
  }

  if (access.status === "denied") {
    return (
      <div className="user-mgmt-page qms-document-library">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: screen?.title || "Document Library" },
          ]}
        />
        <div className="alert alert-warning mt-3">{access.message}</div>
      </div>
    );
  }

  return (
    <div className="user-mgmt-page qms-document-library">
      <InfoAlert {...alertState} onClose={hideAlert} />

      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: screen?.title || "Document Library" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: handleRefresh,
          },
        ]}
        toolbarExtras={
          <button
            type="button"
            className="qms-tour-help-btn"
            data-tour="qms-doc-lib-guide"
            onClick={() => setShowDocLibTour(true)}
            title="Show page guide"
          >
            <i className="fas fa-route" aria-hidden="true" />
            Guide
          </button>
        }
      />

      <div className="qms-doc-lib__panel">
        <div className="qms-doc-lib__report-wrap">
          <ReportTable
            rows={rows}
            fieldOptions={LIBRARY_FIELD_OPTIONS}
            defaultColumns={DEFAULT_LIBRARY_COLUMNS}
            getFieldLabel={(field) =>
              LIBRARY_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderReportCell}
            pageSizeOptions={[10, 25, 50, 100]}
            serverPagination={serverPagination}
            columnSearchMode="server"
            serverColumnSearch={{
              searchableFields: LIBRARY_SERVER_SEARCH_FIELDS,
              debounceMs: 400,
              onChange: handleServerColumnSearch,
            }}
            renderColumnSearch={renderColumnSearch}
            frozenColumnWidthRem={8}
            persistLayoutKey={layoutStorageKey}
            onExportExcel={handleExportToExcel}
            exportLabel="Export Excel"
            tourIds={DOC_LIB_TOUR_IDS}
          />
        </div>
      </div>

      <QmsDocumentLibraryWorkspaceModal
        open={workspaceModal.open}
        onClose={closeDocumentWorkspace}
        row={workspaceModal.row}
        userData={userData}
        activePanel={workspaceModal.panel}
        onPanelChange={handleWorkspacePanelChange}
        documentTitle={
          workspaceModal.row ? getDocumentDisplayName(workspaceModal.row) : ""
        }
        documentCode={
          workspaceModal.row ? getDocumentListId(workspaceModal.row) : ""
        }
        documentUrl={workspaceModal.row?.DocumentURL}
        documentExtension={workspaceModal.row?.DocumentExtension}
        canManageStaff={canManageStaff}
        staff={workspaceModal.staff}
        staffLoading={workspaceModal.staffLoading}
        savingStaffIds={workspaceModal.savingStaffIds}
        onToggleStaff={handleStaffToggle}
        onBulkToggleStaff={handleBulkStaffToggle}
        canManageLearningStaff={canManageLearningStaff}
        learningStaff={workspaceModal.learningStaff}
        learningStaffTrainingRoles={workspaceModal.learningStaffTrainingRoles}
        learningStaffLoading={workspaceModal.learningStaffLoading}
        learningStaffSaving={workspaceModal.learningStaffSaving}
        onMyLearningSave={handleMyLearningSaveDraft}
        onMyLearningDirtyChange={handleMyLearningDirtyChange}
        savingLearningStaffIds={workspaceModal.savingLearningStaffIds}
        onToggleLearningStaff={handleLearningStaffToggle}
        onBulkToggleLearningStaff={handleBulkLearningStaffToggle}
        learningProgressStaff={workspaceModal.learningProgressStaff}
        learningProgressLoading={workspaceModal.learningProgressLoading}
        remindingStaffIds={workspaceModal.remindingStaffIds}
        canSendLearningReminders={canSendLearningReminders}
        onSendLearningReminder={handleSendLearningReminder}
        onSendBulkLearningReminder={handleSendBulkLearningReminder}
        staffId={staffId}
      />

      <IsfPageTour
        open={showDocLibTour}
        steps={docLibTourSteps}
        staffId={staffId}
        markTourCompleted={markQmsDocLibTourCompleted}
        onClose={() => setShowDocLibTour(false)}
      />
    </div>
  );
};

export default QmsDocumentLibrary;
